import {useMutation, useQueryClient} from "@tanstack/react-query"
import {updateBookingStatus} from "api/booking.api"
import {useAuth} from "context/AuthContext"
import {useToast} from "hooks"
import {useState} from "react"
import styled from "styled-components"
import {ButtonPrimary} from "ui/base/Button/ButtonStyled"
import {Modal} from "ui/base/modal/Modal"
import {TextArea} from "ui/base/textArea/TextArea"
import {themeFontSize} from "ui/theme/index.js"

export const AcceptModal = ({show, handleClose, selectedClass}) => {
    const queryClient = useQueryClient()
    const {userData} = useAuth()


    const [text, setText] = useState(
        userData?.teacher?.last_class_instruction || ""
    )
    const [error, setError] = useState("")

    const {mutate, isLoading} = useMutation({
        mutationFn: updateBookingStatus,
        onError: (error) => {
            useToast(
                "error",
                error?.response?.data?.message || "Error: changeClassRequest"
            )
        },
        onSuccess: () => {
            const username =
                selectedClass?.user?.first_name ||
                "" + selectedClass?.user?.last_name?.[0] ||
                ""
            useToast("success", `Your class with ${username} has been accepted`)
            queryClient.invalidateQueries(["getClassRequestForTeacher"])
            queryClient.invalidateQueries(["getFutureClassesForTeacher2"])
            handleClose()
        },
    })

    const handleAccept = () => {
        if (!text.trim()) {
            setError("Instruction message cannot be empty.")
            return
        } else if (text.length < 25) {
            setError("Must have over 25 characters.")
            return
        }
        setError("")
        mutate({
            booking_id: selectedClass?.id,
            status: "accepted",
            class_instruction: text,
        })
    }

    return (
        <>
            <Modal show={show} handleClose={handleClose} minHeight={400} maxWidth={600}>
                <Section>
                    <HeaderWrapper>
                        <ClassInfo>
                            <Info>
                                <Name>Class Details</Name>
                            </Info>
                        </ClassInfo>
                    </HeaderWrapper>
                    <Container>
                        <FormWrapper>
                            <TextArea
                                placeholder={
                                    "This message will be automatically shared with the participant after the class has been booked. Provide detail information the participant should know including specific address for the class and any other relevant information needed."
                                }
                                value={text}
                                setValue={(text) => {
                                    setText(text)
                                    if (text.trim()) setError("")
                                }}
                                maxLength={1000}
                                isError={!!error}
                                errorText={error}
                            />
                        </FormWrapper>

                        <ButtonPrimary disabled={isLoading} onClick={handleAccept}>
                            Accept Class
                        </ButtonPrimary>
                        <ButtonPrimary
                            disabled={isLoading}
                            onClick={handleClose}
                            variant='outline'>
                            Cancel
                        </ButtonPrimary>
                    </Container>
                </Section>
            </Modal>
        </>
    )
}

const Section = styled.div`
    font-family: var(--font-secondary);
    flex-direction: column;
    padding: 1rem;
    display: flex;
    gap: 1rem;
`

const HeaderWrapper = styled.div`
    flex-direction: column;
    display: flex;
    gap: 1rem;
`

const ClassInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    gap: 1rem;
`

const Info = styled.div`
    flex: 1;
    display: grid;
    gap: 1rem;
`

const Name = styled.div`
    font-size: ${themeFontSize("regular")};
    font-weight: bold;
`

const Container = styled.div`
    display: grid;
    gap: 1rem;
`

const FormWrapper = styled.div`
    display: grid;
    gap: 1rem;

    & textarea {
        min-height: 200px;
    }
`
